import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { MonthProvider } from '@/contexts/MonthContext';
import { ThemeProvider } from '@/components/ThemeProvider';
import { Toaster } from 'sonner';
import Layout from '@/components/Layout';
import LoginPage from '@/pages/LoginPage';
import SignupPage from '@/pages/SignupPage';
// import AuthCallbackPage from '@/pages/AuthCallbackPage'; // Google SSO disabled
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import VerifyEmailPage from '@/pages/VerifyEmailPage';
import AuthCallbackPage from '@/pages/AuthCallbackPage';
import UpdateEmailPage from '@/pages/UpdateEmailPage';
import ChangePasswordPage from '@/pages/ChangePasswordPage';
import DashboardPage from '@/pages/DashboardPage';
import TransactionsPage from '@/pages/TransactionsPage';
import UploadPage from '@/pages/UploadPage';
import IncomePage from '@/pages/IncomePage';
import NetWorthPage from '@/pages/NetWorthPage';
import AnalyticsPage from '@/pages/AnalyticsPage';
import AdminPage from '@/pages/AdminPage';
import AuditLogPage from '@/pages/AuditLogPage';
import InvestmentsPage from '@/pages/InvestmentsPage';
import SettingsPage from '@/pages/SettingsPage';
import AboutPage from '@/pages/AboutPage';
import AdminHealthPage from '@/pages/AdminHealthPage';
import NotificationsPage from '@/pages/NotificationsPage';
import SupportPage from '@/pages/SupportPage';
import FeedbackPage from '@/pages/FeedbackPage';
import PaymentSuccessPage from '@/pages/PaymentSuccessPage';
import PaymentCancelPage from '@/pages/PaymentCancelPage';
import AdminSubscriptionsPage from '@/pages/AdminSubscriptionsPage';
import TermsPage from '@/pages/TermsPage';
import PrivacyPage from '@/pages/PrivacyPage';
import '@/App.css';

function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  
  // NEW: Check if user needs to add/verify email
  if (user.email_verification_required && !user.email_verified) {
    return <Navigate to="/update-email" replace />;
  }
  
  if (adminOnly && user.role !== 'admin') return <Navigate to="/dashboard" replace />;

  return <Layout>{children}</Layout>;
}

function AuthRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (user && !user.must_change_password) return <Navigate to="/dashboard" replace />;
  if (user && user.must_change_password) return <Navigate to="/change-password" replace />;
  return children;
}

function PasswordRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!user.must_change_password) return <Navigate to="/dashboard" replace />;
  return children;
}

function UpdateEmailRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRoutes() {
  const location = useLocation();
  
  // Google SSO temporarily disabled
  // if (location.hash?.includes('session_id=')) {
  //   return <AuthCallbackPage />;
  // }
  
  return (
    <Routes>
      <Route path="/login" element={<AuthRoute><LoginPage /></AuthRoute>} />
      <Route path="/signup" element={<AuthRoute><SignupPage /></AuthRoute>} />
      <Route path="/forgot-password" element={<AuthRoute><ForgotPasswordPage /></AuthRoute>} />
      {/* Public legal pages — accessible without authentication */}
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      {/* Google SSO disabled: <Route path="/auth/callback" element={<AuthCallbackPage />} /> */}
      <Route path="/change-password" element={<PasswordRoute><ChangePasswordPage /></PasswordRoute>} />
      
      {/* Email verification route - accessible even if not fully authenticated */}
      <Route path="/update-email" element={<UpdateEmailRoute><UpdateEmailPage /></UpdateEmailRoute>} />
      
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/transactions" element={<ProtectedRoute><TransactionsPage /></ProtectedRoute>} />
      <Route path="/upload" element={<ProtectedRoute><UploadPage /></ProtectedRoute>} />
      <Route path="/income" element={<ProtectedRoute><IncomePage /></ProtectedRoute>} />
      <Route path="/investments" element={<ProtectedRoute><InvestmentsPage /></ProtectedRoute>} />
      <Route path="/networth" element={<ProtectedRoute><NetWorthPage /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/about" element={<ProtectedRoute><AboutPage /></ProtectedRoute>} />
      <Route path="/support" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />
      <Route path="/feedback" element={<ProtectedRoute><FeedbackPage /></ProtectedRoute>} />
      <Route path="/payment/success" element={<PaymentSuccessPage />} />
      <Route path="/payment/cancel" element={<PaymentCancelPage />} />
      <Route path="/admin/health" element={<ProtectedRoute adminOnly><AdminHealthPage /></ProtectedRoute>} />
      <Route path="/admin/notifications" element={<ProtectedRoute adminOnly><NotificationsPage /></ProtectedRoute>} />
      <Route path="/admin/audit" element={<ProtectedRoute adminOnly><AuditLogPage /></ProtectedRoute>} />
      <Route path="/admin/subscriptions" element={<ProtectedRoute adminOnly><AdminSubscriptionsPage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute adminOnly><AdminPage /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <MonthProvider>
            <AppRoutes />
            <Toaster position="top-right" richColors closeButton />
          </MonthProvider>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
